import { getBackendGraphqlUrl } from './backendUrl'

type GraphQLResponse<T> = {
  data: T
  errors?: Array<{ message: string }>
}

type GraphQLCacheEntry<TData> = {
  expiresAt: number
  promise: Promise<TData>
}

const DEFAULT_CI_GRAPHQL_CACHE_TTL_MS = 60_000
const graphqlQueryCache = new Map<string, GraphQLCacheEntry<unknown>>()

function getCiGraphqlCacheTtlMs() {
  const configuredTtl = Number.parseInt(
    process.env.PLAYWRIGHT_CI_GRAPHQL_CACHE_TTL_MS ?? '',
    10
  )

  if (Number.isFinite(configuredTtl) && configuredTtl >= 0) {
    return configuredTtl
  }

  return DEFAULT_CI_GRAPHQL_CACHE_TTL_MS
}

function isQueryOperation(query: string) {
  return /^\s*query\b/.test(query)
}

function hasSensitiveHeaders(headers: HeadersInit | undefined) {
  if (!headers) return false

  const sensitiveHeaders = ['authorization', 'cookie']

  if (headers instanceof Headers) {
    return sensitiveHeaders.some(header => headers.has(header))
  }

  if (Array.isArray(headers)) {
    return headers.some(([name]) =>
      sensitiveHeaders.includes(name.toLowerCase())
    )
  }

  return Object.keys(headers).some(name =>
    sensitiveHeaders.includes(name.toLowerCase())
  )
}

function shouldUseCiGraphqlCache(
  query: string,
  fetchOptions: RequestInit,
  cacheTtlMs: number
) {
  return (
    process.env.CI === 'true' &&
    cacheTtlMs > 0 &&
    fetchOptions.cache !== 'no-store' &&
    isQueryOperation(query) &&
    !hasSensitiveHeaders(fetchOptions.headers)
  )
}

async function executeGraphqlRequest<TData>(
  queryBody: string,
  fetchOptions: RequestInit,
  timeoutMs: number,
  backendGraphqlUrl: string
): Promise<TData> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(backendGraphqlUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions?.headers,
      },
      ...fetchOptions,
      body: queryBody,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    const responseText = await response.text()
    const parsed = JSON.parse(responseText) as GraphQLResponse<TData>

    const { data, errors } = parsed
    if (errors) {
      throw new Error(errors.map(e => e.message).join('\n'))
    }

    if (!data) {
      throw new Error('No data returned from GraphQL server')
    }

    return data
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`GraphQL request timed out after ${timeoutMs / 1000} seconds`)
    }
    throw error
  }
}

export async function graphqlRequest<
  TData,
  TVariables = Record<string, unknown>,
>(
  query: string,
  variables?: TVariables,
  options?: RequestInit & { timeoutMs?: number }
): Promise<TData> {
  const queryBody = JSON.stringify({
    query,
    variables,
  })

  const { timeoutMs = 30000, ...fetchOptions } = options ?? {}
  const backendGraphqlUrl = getBackendGraphqlUrl()
  const cacheTtlMs = getCiGraphqlCacheTtlMs()

  if (shouldUseCiGraphqlCache(query, fetchOptions, cacheTtlMs)) {
    const now = Date.now()
    const cacheKey = `${backendGraphqlUrl}\n${queryBody}`
    const cached = graphqlQueryCache.get(cacheKey)

    if (cached && cached.expiresAt > now) {
      return cached.promise as Promise<TData>
    }

    if (cached) {
      graphqlQueryCache.delete(cacheKey)
    }

    const promise = executeGraphqlRequest<TData>(
      queryBody,
      fetchOptions,
      timeoutMs,
      backendGraphqlUrl
    )

    graphqlQueryCache.set(cacheKey, {
      expiresAt: now + cacheTtlMs,
      promise,
    })

    promise.catch(() => {
      if (graphqlQueryCache.get(cacheKey)?.promise === promise) {
        graphqlQueryCache.delete(cacheKey)
      }
    })

    return promise
  }

  return executeGraphqlRequest<TData>(
    queryBody,
    fetchOptions,
    timeoutMs,
    backendGraphqlUrl
  )
}
